/**
 * Supabase persistence for component instances.
 *
 * Write-through cache: Zustand updates immediately (UI), Supabase syncs in background.
 * All writes are debounced (1s wait, 10s max) and batched into a single upsert call.
 * Deletes execute before upserts. Later writes for the same key overwrite earlier ones.
 *
 * Table: canvas_instances (PK: canvas_id, component_file_key, storyboard_id, instance_key)
 * TTL: 1 month (pg_cron cleanup)
 */

import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import type { Instance } from "./instancesStore";
import { getInstanceUniqueKey } from "./instancesStore";
import { debounce } from "lodash-es";

// ═══════════════════════════════════════════════════════════════════════════════
// BATCHED WRITE QUEUE
// ═══════════════════════════════════════════════════════════════════════════════

// Pending upserts: compositeKey -> row data
// Using a Map ensures later writes for the same key overwrite earlier ones
const pendingUpserts = new Map<
  string,
  {
    canvas_id: string;
    component_file_key: string;
    storyboard_id: string;
    instance_key: string;
    instance_data: Instance;
  }
>();

// Pending deletes for storyboards (delete all instances for a storyboard)
// Key: `${canvasId}:${componentFileKey}:${storyboardId}`
const pendingStoryboardDeletes = new Set<string>();

// Pending deletes for components (delete all instances for a component)
// Key: `${canvasId}:${componentFileKey}`
const pendingComponentDeletes = new Set<string>();

// Pending "delete invalid storyboards" operations
// Key: `${canvasId}:${componentFileKey}` -> Set of valid storyboard IDs
const pendingInvalidStoryboardDeletes = new Map<string, Set<string>>();

/**
 * Flush all pending writes to Supabase in one batch.
 */
async function flushPendingWrites() {
  const supabase = getSupabaseClient();
  if (!supabase) return;

  // Snapshot and clear queues atomically
  const upserts = Array.from(pendingUpserts.values());
  const storyboardDeletes = new Set(pendingStoryboardDeletes);
  const componentDeletes = new Set(pendingComponentDeletes);
  const invalidStoryboardDeletes = new Map(pendingInvalidStoryboardDeletes);

  pendingUpserts.clear();
  pendingStoryboardDeletes.clear();
  pendingComponentDeletes.clear();
  pendingInvalidStoryboardDeletes.clear();

  // Execute deletes first (component deletes take priority over storyboard deletes)
  const deletePromises: PromiseLike<unknown>[] = [];

  // Component-level deletes: batch by canvasId to reduce API calls
  // Group: canvasId -> componentFileKeys[]
  const componentDeletesByCanvas = new Map<string, string[]>();
  for (const key of componentDeletes) {
    const [canvasId, componentFileKey] = key.split(":", 2);
    const existing = componentDeletesByCanvas.get(canvasId) || [];
    existing.push(componentFileKey);
    componentDeletesByCanvas.set(canvasId, existing);
  }

  for (const [canvasId, componentFileKeys] of componentDeletesByCanvas) {
    deletePromises.push(
      supabase
        .from("canvas_instances")
        .delete()
        .eq("canvas_id", canvasId)
        .in("component_file_key", componentFileKeys)
        .then(),
    );
  }

  // Storyboard-level deletes: batch by (canvasId, componentFileKey) to reduce API calls
  // Group: canvasId:componentFileKey -> storyboardIds[]
  const storyboardDeletesByComponent = new Map<string, string[]>();
  for (const key of storyboardDeletes) {
    const [canvasId, componentFileKey, storyboardId] = key.split(":", 3);
    const componentKey = `${canvasId}:${componentFileKey}`;
    if (componentDeletes.has(componentKey)) continue; // Skip, component being deleted

    const existing = storyboardDeletesByComponent.get(componentKey) || [];
    existing.push(storyboardId);
    storyboardDeletesByComponent.set(componentKey, existing);
  }

  for (const [componentKey, storyboardIds] of storyboardDeletesByComponent) {
    const [canvasId, componentFileKey] = componentKey.split(":", 2);
    deletePromises.push(
      supabase
        .from("canvas_instances")
        .delete()
        .eq("canvas_id", canvasId)
        .eq("component_file_key", componentFileKey)
        .in("storyboard_id", storyboardIds)
        .then(),
    );
  }

  // Invalid storyboard deletes (skip if component is being deleted entirely)
  for (const [key, validIds] of invalidStoryboardDeletes) {
    const [canvasId, componentFileKey] = key.split(":", 2);
    const componentKey = `${canvasId}:${componentFileKey}`;
    if (componentDeletes.has(componentKey)) continue;
    if (validIds.size === 0) continue;

    deletePromises.push(
      supabase
        .from("canvas_instances")
        .delete()
        .eq("canvas_id", canvasId)
        .eq("component_file_key", componentFileKey)
        .not("storyboard_id", "in", `(${[...validIds].join(",")})`)
        .then(),
    );
  }

  // Wait for deletes to complete before upserting
  if (deletePromises.length > 0) {
    await Promise.all(deletePromises);
  }

  // Filter out upserts for deleted components/storyboards
  const filteredUpserts = upserts.filter((row) => {
    const componentKey = `${row.canvas_id}:${row.component_file_key}`;
    if (componentDeletes.has(componentKey)) return false;

    const storyboardKey = `${row.canvas_id}:${row.component_file_key}:${row.storyboard_id}`;
    if (storyboardDeletes.has(storyboardKey)) return false;

    return true;
  });

  // Batch upsert all pending instances
  if (filteredUpserts.length > 0) {
    await supabase.from("canvas_instances").upsert(filteredUpserts, {
      onConflict: "canvas_id,component_file_key,storyboard_id,instance_key",
    });
  }
}

// Debounced flush: 1s debounce with 10s max wait
const debouncedFlush = debounce(flushPendingWrites, 1000, { maxWait: 10000 });

/**
 * Get the Supabase client from the store.
 * Returns undefined if not yet initialized.
 */
function getSupabaseClient() {
  return useSupabaseStore.getState().supabaseClient;
}

// ═══════════════════════════════════════════════════════════════════════════════
// PUBLIC API (called by instancesStore)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Rehydrate all instances for a canvas from Supabase.
 * This is NOT debounced - it's a read operation that happens once on load.
 *
 * @param canvasId - The canvas to load instances for
 * @param validStoryboardIds - Set of storyboard IDs that currently exist in pages.
 *   If non-empty, instances for storyboards NOT in this set will be deleted.
 *   If empty (pages not loaded yet), no cleanup is performed.
 */
export async function rehydrateFromSupabase(
  canvasId: string,
  validStoryboardIds: Set<string>,
): Promise<Record<string, Record<string, Record<string, Instance>>>> {
  const supabase = getSupabaseClient();
  if (!supabase) return {};

  // Clean up orphaned storyboard instances (only if we have actual storyboard data)
  if (validStoryboardIds.size > 0) {
    const validIds = [...validStoryboardIds];
    await supabase
      .from("canvas_instances")
      .delete()
      .eq("canvas_id", canvasId)
      .not("storyboard_id", "in", `(${validIds.join(",")})`);
  }

  // Load all instances for this canvas
  const { data, error } = await supabase
    .from("canvas_instances")
    .select("component_file_key, storyboard_id, instance_key, instance_data")
    .eq("canvas_id", canvasId);

  if (error || !data) return {};

  // Rebuild nested structure: componentFileKey -> storyboardId -> instanceKey -> Instance
  const result: Record<string, Record<string, Record<string, Instance>>> = {};
  for (const row of data) {
    const { component_file_key, storyboard_id, instance_key, instance_data } =
      row;
    result[component_file_key] ??= {};
    result[component_file_key][storyboard_id] ??= {};
    result[component_file_key][storyboard_id][instance_key] =
      instance_data as Instance;
  }
  return result;
}

/**
 * Queue upsert for instances in a storyboard.
 * If instances is empty, queues deletion of all instances for that storyboard.
 */
export function upsertInstances(
  canvasId: string,
  componentFileKey: string,
  storyboardId: string,
  instances: Instance[],
) {
  if (instances.length === 0) {
    // Queue deletion of all instances for this storyboard
    const key = `${canvasId}:${componentFileKey}:${storyboardId}`;
    pendingStoryboardDeletes.add(key);
  } else {
    // Queue upserts for each instance
    for (const instance of instances) {
      const instanceKey = getInstanceUniqueKey(instance, canvasId);
      const compositeKey = `${canvasId}:${componentFileKey}:${storyboardId}:${instanceKey}`;
      pendingUpserts.set(compositeKey, {
        canvas_id: canvasId,
        component_file_key: componentFileKey,
        storyboard_id: storyboardId,
        instance_key: instanceKey,
        instance_data: instance,
      });
    }
  }

  debouncedFlush();
}

/**
 * Queue update for a single instance.
 */
export function updateInstance(
  canvasId: string,
  componentFileKey: string,
  storyboardId: string,
  instanceKey: string,
  instance: Instance,
) {
  const compositeKey = `${canvasId}:${componentFileKey}:${storyboardId}:${instanceKey}`;
  pendingUpserts.set(compositeKey, {
    canvas_id: canvasId,
    component_file_key: componentFileKey,
    storyboard_id: storyboardId,
    instance_key: instanceKey,
    instance_data: instance,
  });

  debouncedFlush();
}

/**
 * Queue deletion of instances for storyboards that are no longer valid.
 */
export function deleteInvalidStoryboards(
  canvasId: string,
  componentFileKey: string,
  validStoryboardIds: string[],
) {
  if (validStoryboardIds.length === 0) return;

  const key = `${canvasId}:${componentFileKey}`;
  // Merge with existing valid IDs (intersection would be more complex, just replace)
  pendingInvalidStoryboardDeletes.set(key, new Set(validStoryboardIds));

  debouncedFlush();
}

/**
 * Queue deletion of all instances for a component.
 */
export function deleteInstancesForComponent(
  canvasId: string,
  componentFileKey: string,
) {
  const key = `${canvasId}:${componentFileKey}`;
  pendingComponentDeletes.add(key);

  debouncedFlush();
}

/**
 * Load instances for a specific component from Supabase.
 * Used for component-level rehydration (when a component is selected).
 *
 * Returns: storyboardId -> instanceKey -> Instance
 */
export async function loadComponentInstancesFromSupabase(
  canvasId: string,
  componentFileKey: string,
): Promise<Record<string, Record<string, Instance>>> {
  const supabase = getSupabaseClient();
  if (!supabase) return {};

  const { data, error } = await supabase
    .from("canvas_instances")
    .select("storyboard_id, instance_key, instance_data")
    .eq("canvas_id", canvasId)
    .eq("component_file_key", componentFileKey);

  if (error || !data) return {};

  // Rebuild nested structure: storyboardId -> instanceKey -> Instance
  const result: Record<string, Record<string, Instance>> = {};
  for (const row of data) {
    const { storyboard_id, instance_key, instance_data } = row;
    result[storyboard_id] ??= {};
    result[storyboard_id][instance_key] = instance_data as Instance;
  }
  return result;
}
